import { useCallback } from "react";

import type { DayOfTheWeek, OverSiteControllerPeoplePriceInput } from "@/lib/gql/__generated__/graphql";
import { type OverSiteControllerPeopleNumInput } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType, ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import { DAY_OF_THE_WEEK_LIST } from "@/components/features/items/edit/_shared/constants/day-of-the-week";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import { useProductRoomFormModelContext } from "../../../../../../context";
import { createEmptyPriceItem } from "../../../_constants/empty-price-item";

import type { CheckedState } from "@radix-ui/react-checkbox";

export const useWeekendSettings = () => {
	const { viewModel, onSetViewModel } = useProductRoomFormModelContext();
	const { overSiteControllerPeopleNum, capacityPeopleMaximum: _capacityPeopleMaximum } = viewModel;
	const capacityPeopleMaximum = _capacityPeopleMaximum.value;

	const weekend = overSiteControllerPeopleNum?.value?.[0];
	const weekday = overSiteControllerPeopleNum?.value?.[1];
	const weekendPrices = weekend?.prices;

	const canAppendItem = weekendPrices?.value.length
		? Number(weekendPrices.value.at(-1)?.upperNum.value) < Number(capacityPeopleMaximum)
		: true;

	const availableWeekend = weekend?.availableWeekDays.value ?? [];
	const rawAvailableWeekend = availableWeekend.map((item) => item.value);
	const weekendPriceFieldOptions = DAY_OF_THE_WEEK_LIST.map((item) => {
		return {
			...item,
			id: `weekendPrice_${item.label}`,
		};
	});

	const defaultValue = createEmptyPriceItem(Number(capacityPeopleMaximum));

	/**
	 * 休日価格のみを更新する（平日価格は更新しない）
	 */
	const onSetWeekendSettings = useCallback(
		(input: ForAutoGenerator<OverSiteControllerPeopleNumInput, "">) => {
			if (weekend && weekday) {
				onSetViewModel([
					{
						key: "overSiteControllerPeopleNum",
						value: {
							isEdited: true,
							hasError: false,
							value: [input, weekday],
						},
					},
				]);
			}
		},
		[onSetViewModel, weekday, weekend],
	);

	/**
	 * 休日価格に新しい項目を追加する
	 */
	const appendItem = useCallback(() => {
		if (weekend) {
			onSetWeekendSettings({
				...weekend,
				prices: {
					isEdited: true,
					hasError: false,
					value: weekendPrices ? [...weekendPrices.value, defaultValue] : [defaultValue],
				} satisfies ForArrayType<OverSiteControllerPeoplePriceInput[]>,
			});
		}
	}, [defaultValue, onSetWeekendSettings, weekend, weekendPrices]);

	/**
	 * 休日価格の項目の内、特定の項目を削除する（index）
	 */
	const removeItem = useCallback(
		(index: number) => {
			if (weekend) {
				onSetWeekendSettings({
					...weekend,
					prices: {
						isEdited: true,
						hasError: false,
						value: weekendPrices ? weekendPrices.value.filter((_, i) => i !== index) : [],
					} satisfies ForArrayType<OverSiteControllerPeoplePriceInput[]>,
				});
			}
		},
		[onSetWeekendSettings, weekend, weekendPrices],
	);

	/**
	 * 休日価格の項目の内、特定の項目の上限人数を更新する
	 */
	const onSetWeekendUpperNum = (input: string, index: number) => {
		if (weekend) {
			const copy = { ...weekend };

			const newValue = {
				...copy.prices,
				value: copy.prices.value.map((item, i) =>
					i === index
						? {
								price: item.price,
								upperNum: {
									isEdited: true,
									value: Number(input),
								},
							}
						: item,
				),
			};

			onSetWeekendSettings({ ...weekend, prices: newValue });
		}
	};

	const onSetWeekendPriceInput = (input: number | undefined, index: number) => {
		if (weekend) {
			const copy = { ...weekend };

			const newValue = {
				...copy.prices,
				value: copy.prices.value.map((item, i) =>
					i === index
						? {
								upperNum: item.upperNum,
								price: {
									...item.price,
									amount: {
										isEdited: true,
										value: input!,
									},
								},
							}
						: item,
				),
			};

			onSetWeekendSettings({ ...weekend, prices: newValue });
		}
	};

	/**
	 * 休日価格にする曜日を更新する（休日価格に設定されていない曜日は平日価格の曜日になるように更新する必要がある）
	 */
	const onSetAvailableWeekend = (checked: CheckedState, item: FormOptionType) => {
		if (weekend && weekday) {
			// 最新の休日にする曜日の設定
			const rawNewAvailableWeekend = checked
				? [...new Set([...rawAvailableWeekend, item.value as DayOfTheWeek])]
				: rawAvailableWeekend.filter((value) => value !== (item.value as DayOfTheWeek));

			// 最新の平日にする曜日の設定
			const rawNewWeekendAvailableWeekDays = DAY_OF_THE_WEEK_LIST.filter(
				(item) => !rawNewAvailableWeekend?.includes(item.value),
			).map((item) => item.value);

			const newAvailableWeekend = rawNewAvailableWeekend.map((item) => {
				return {
					isEdited: true,
					value: item,
				};
			});

			const newWeekendAvailableWeekDays = rawNewWeekendAvailableWeekDays.map((item) => {
				return {
					isEdited: true,
					value: item,
				};
			});

			onSetViewModel([
				{
					key: "overSiteControllerPeopleNum",
					value: {
						isEdited: true,
						hasError: false,
						value: [
							{
								...weekend,
								availableWeekDays: {
									isEdited: true,
									hasError: false,
									value: newAvailableWeekend,
								},
							},
							{
								...weekday,
								availableWeekDays: {
									isEdited: true,
									hasError: false,
									value: newWeekendAvailableWeekDays,
								},
							},
						],
					},
				},
			]);
		}
	};

	return {
		onSetAvailableWeekend,
		rawAvailableWeekend,
		weekendPriceFieldOptions,
		onSetWeekendPriceInput,
		capacityPeopleMaximum: Number(capacityPeopleMaximum),
		weekendPrices,
		appendItem,
		canAppendItem,
		removeItem,
		onSetWeekendUpperNum,
	};
};
